// Valor de mercado, salario e contrato.
//
// A escala e a mesma do 38 Brasil: dinheiro em MILHOES de reais, e o valor do
// atleta sai do rating, da idade e do potencial. Aqui os numeros foram
// reescalados para basquete -- elenco de 15 em vez de 25, e teto salarial que
// aperta de verdade.

import { makeRng, clamp } from './rng.js';

export const MONEY = {
  // Teto salarial SUAVE: passar dele nao e proibido, custa imposto de luxo.
  // E o que faz o mercado de basquete ser diferente do de futebol -- comprar
  // bem nao basta, tem que caber na folha.
  salaryCap: 140,        // folha anual, em milhoes
  luxuryTaxRate: 1.5,    // cada milhao acima do teto custa 1,5 na virada
  hardFloor: 105,        // piso de folha: abaixo disso, multa da liga
  minRoster: 12,
  maxRoster: 15,
  askMultiplier: 1.08,   // preco pedido sobre o valor de mercado
  fairMultiplier: 1.16,  // ate aqui o vendedor considera oferta justa
  stretchMultiplier: 1.36, // teto do que um comprador estica
  starStretch: 1.48,     // ...para um astro
};

/**
 * Valor de mercado em milhoes.
 * Curva por rating (exponencial: os ultimos pontos de overall custam caro),
 * corrigida por idade e por potencial ainda nao entregue.
 */
export function marketValueOf(player, { season = 1 } = {}) {
  const ovr = player.overall;
  const age = player.age;
  const potential = player.potential ?? ovr;

  // 60 -> ~1,2  |  75 -> ~12  |  85 -> ~45  |  92 -> ~95
  const base = Math.pow(Math.max(ovr - 46, 1), 2.34) / 58;

  // Idade: pico de valor entre 24 e 28.
  let ageMult;
  if (age <= 21) ageMult = 1.22;
  else if (age <= 23) ageMult = 1.16;
  else if (age <= 28) ageMult = 1.0;
  else if (age <= 31) ageMult = 0.82;
  else if (age <= 34) ageMult = 0.58;
  else ageMult = 0.34;

  // Potencial nao entregue vale dinheiro, e vale mais em quem e jovem.
  const gap = Math.max(0, potential - ovr);
  const upside = 1 + (gap * (age <= 24 ? 0.028 : age <= 28 ? 0.014 : 0.004));

  const value = base * ageMult * upside;
  void season;
  return Math.round(clamp(value, 0.2, 260) * 10) / 10;
}

/** Salario a partir de um valor de mercado NAO escalado. */
export function wageFromValue(value) {
  return 0.8 + Math.pow(value, 0.72) * 0.445;
}

/**
 * Salario anual do atleta. Se ele ja tem salario gravado, e esse que vale --
 * recalcular por cima ignoraria a escala da liga e o que foi negociado.
 */
export function wageOf(player) {
  if (typeof player.wage === 'number' && player.wage > 0) return player.wage;
  const value = player.marketValue ?? marketValueOf(player);
  return Math.round(clamp(wageFromValue(value), 0.1, 62) * 10) / 10;
}

/** Anos de contrato restantes na criacao do elenco. */
export function initialContract(player, seed) {
  const rng = makeRng(`contrato|${seed}|${player.id}`);
  if (player.age >= 34) return 1 + rng.int(2);
  if (player.age <= 22) return 2 + rng.int(3);
  return 1 + rng.int(4);
}

/**
 * Preenche potencial, valor, salario e contrato.
 * `scale` e a escala de dinheiro da liga: valor e salario sao calculados na
 * escala da NBA e escalados JUNTOS, para a relacao entre eles nao mudar de
 * uma liga para a outra.
 */
export function enrichPlayer(player, { season = 1, seed = 'x', scale = 1 } = {}) {
  const rng = makeRng(`potencial|${seed}|${player.id}`);
  // Potencial: quem e jovem tem mais teto acima do que ja mostra.
  const room = player.age <= 21 ? 14 : player.age <= 24 ? 9 : player.age <= 27 ? 4 : 1;
  player.potential = Math.round(clamp(player.overall + rng() * room, player.overall, 99));
  const rawValue = marketValueOf(player, { season });
  player.marketValue = Math.round(clamp(rawValue * scale, 0.05, 400) * 10) / 10;
  player.wage = Math.round(clamp(wageFromValue(rawValue) * scale, 0.1, 90) * 10) / 10;
  player.contractYears = initialContract(player, seed);
  return player;
}

/** Folha salarial anual de um elenco. */
export function payrollOf(roster) {
  return Math.round(roster.reduce((a, p) => a + (p.wage || 0), 0) * 10) / 10;
}

/**
 * Economia da liga em que a carreira acontece. A NBA e o NBB tem tetos e
 * tamanhos de elenco diferentes -- nenhuma funcao pode assumir os da NBA.
 */
export function econOf(config) {
  return { ...MONEY, ...(config?.economy || {}) };
}

/** Imposto de luxo devido na virada de temporada. */
export function luxuryTaxOf(roster, econ = MONEY) {
  const payroll = payrollOf(roster);
  if (payroll <= econ.salaryCap) return 0;
  return Math.round((payroll - econ.salaryCap) * econ.luxuryTaxRate * 10) / 10;
}

/** Formata dinheiro do jeito que o jogo mostra. */
export function money(millions) {
  const v = Number(millions) || 0;
  if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toFixed(2)} bi`;
  if (Math.abs(v) >= 1) return `R$ ${v.toFixed(1)} mi`;
  return `R$ ${(v * 1000).toFixed(0)} mil`;
}
